import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import ShopType from "@/enum/ShopType";
import { isReissueSupported, isReshipSupported, preShipShopTypes } from "@/utils/ShopUtils";
import { useOtherOperationActions } from "../hooks/useOtherOperationActions";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import {
    getAvailableOtherOperationKeys,
    OperationKey,
    OperationLabelClsMap,
    OperationLabelNameMap,
} from "../services/otherOperationDefinitions";
import classNames from "classnames";

interface FavoriteOperationsButtonsProps {
    tab: OrderTab;
    shop: ShopDTO;
    getSelectedOrders(): OrderDTO[];
    isSupplierShop: boolean;
}

const FavoriteOperationsButtons: React.FC<FavoriteOperationsButtonsProps> = function (props) {
    const { tab, shop, getSelectedOrders, isSupplierShop } = props;

    const orderManagementStore = useOrderManagementStore();
    const { storage } = orderManagementStore;

    const favoriteKeys = storage.favoriteOperationKeysCache;
    const hasStoredValue = !!storage.favoriteOperationKeysStoredCache;

    const {
        runOperationByKey,
        loading,
        isRetailer,
        fulfillmentType,
        showPackListExport,
        showBatchSubmitOrder,
        showBatchPayOrder,
        companyId,
    } = useOtherOperationActions({
        tab,
        shop,
        getSelectedOrders,
    });

    const visibleKeys = getAvailableOtherOperationKeys({
        tab,
        shop,
        isSupplierShop,
        isRetailer,
        fulfillmentType,
        showPackListExport,
        showBatchSubmitOrder,
        showBatchPayOrder,
        companyId,
    });
    const visibleSet = new Set(visibleKeys);
    const defaultFavoriteKeys: OperationKey[] = [];
    const showDefaultPrintPackingList = tab !== OrderTab.PRE_SHIP;
    const showDefaultPreShip = tab === OrderTab.PENDING_SHIPMENT && preShipShopTypes.includes(shop.type);
    const showDefaultReissuePackage = [OrderTab.SUCCESS, OrderTab.SHIPPED].includes(tab) && isReissueSupported(shop);
    const showDefaultModifyWaybill = tab === OrderTab.SHIPPED && isReshipSupported(shop);
    const showDefaultExportShippingTemplate =
        tab !== OrderTab.PRE_SHIP && [ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL].includes(shop.type);

    if (showDefaultPrintPackingList) {
        defaultFavoriteKeys.push(OperationKey.PRINT_PACKING_LIST);
    }

    if (showDefaultPreShip) {
        defaultFavoriteKeys.push(OperationKey.PRE_SHIP);
    }

    if (showDefaultReissuePackage) {
        defaultFavoriteKeys.push(OperationKey.REISSUE_PACKAGE);
    }

    if (showDefaultModifyWaybill) {
        defaultFavoriteKeys.push(OperationKey.RESHIP);
    }

    if (showDefaultExportShippingTemplate) {
        defaultFavoriteKeys.push(OperationKey.EXPORT_SHIPPING_TEMPLATE);
    }

    useEffect(() => {
        if (!hasStoredValue && favoriteKeys.length === 0 && defaultFavoriteKeys.length) {
            storage.setFavoriteOperationKeys(defaultFavoriteKeys);
        }
    }, [hasStoredValue, favoriteKeys.length, defaultFavoriteKeys.join()]);

    const availableKeys = favoriteKeys.length
        ? favoriteKeys.filter((key) => visibleSet.has(key))
        : !hasStoredValue
        ? defaultFavoriteKeys.filter((key) => visibleSet.has(key))
        : [];

    if (!availableKeys.length) {
        return null;
    }

    return (
        <>
            {availableKeys.map((key) => (
                <AutoLoadingButton
                    key={key}
                    className={classNames("mr3", OperationLabelClsMap[key])}
                    disabled={loading}
                    onClick={() => runOperationByKey(key)}
                >
                    {key === OperationKey.RESHIP ? "修改单号" : OperationLabelNameMap[key]}
                    {key === OperationKey.RESHIP ? <span className="small">(重新发货)</span> : null}
                </AutoLoadingButton>
            ))}
        </>
    );
};

export default observer(FavoriteOperationsButtons);
